import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const RoleBasedSidebar = ({ isCollapsed = false, onToggle, userRole = 'admin' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const menuItems = [
    {
      label: 'Dashboard',
      path: '/dashboard-principal',
      icon: 'LayoutDashboard',
      roles: ['admin', 'caja', 'ventas', 'tecnico'],
      tooltip: 'Resumen de operaciones diarias'
    },
    {
      label: 'Caja',
      path: '/caja',
      icon: 'DollarSign',
      roles: ['admin', 'caja'],
      tooltip: 'Gestión de pagos y transacciones'
    },
    {
      label: 'Órdenes',
      path: '/order-management',
      icon: 'ClipboardList',
      roles: ['admin', 'ventas', 'tecnico'],
      tooltip: 'Gestión de órdenes de servicio'
    },
    {
      label: 'Cotizaciones',
      path: '/quotes-management',
      icon: 'FileText',
      roles: ['admin', 'ventas'],
      tooltip: 'Gestión de cotizaciones pendientes'
    },
    {
      label: 'Inventario',
      path: '/inventario',
      icon: 'Package',
      roles: ['admin', 'ventas'],
      tooltip: 'Control de stock y movimientos'
    },
    {
      label: 'Clientes',
      path: '/clientes',
      icon: 'Users',
      roles: ['admin', 'ventas'],
      tooltip: 'Gestión de clientes y vehículos'
    },
    {
      label: 'Reportes',
      path: '/reportes',
      icon: 'BarChart3',
      roles: ['admin', 'caja'],
      tooltip: 'Informes y estadísticas'
    },
    {
      label: 'Productos',
      path: '/productos',
      icon: 'Settings',
      roles: ['admin'],
      tooltip: 'Configuración de servicios y productos'
    },
    {
      label: 'Usuarios',
      path: '/usuarios',
      icon: 'UserCog',
      roles: ['admin'],
      tooltip: 'Administración de usuarios'
    }
  ];

  const filteredMenuItems = menuItems?.filter(item => 
    item?.roles?.includes(userRole)
  );

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobileOpen) {
      setIsMobileOpen(false);
    }
  };

  const handleMobileToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileOpen]);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileOpen]);

  return (
    <>
      <button
        onClick={handleMobileToggle}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-lg bg-card shadow-elevation-2 focus-ring"
        aria-label="Toggle menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} />
      </button>
      {isMobileOpen && (
        <div 
          className="mobile-overlay"
          onClick={handleMobileToggle}
          aria-hidden="true"
        />
      )}
      <aside
        className={`
          fixed lg:fixed top-0 left-0 h-full bg-card border-r border-border
          transition-all duration-250 ease-smooth z-[1000] lg:z-[100]
          ${isCollapsed ? 'w-20' : 'w-60'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'sidebar-collapsed' : ''}
        `}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Icon name="Wrench" size={24} color="var(--color-primary)" />
          </div>
          {!isCollapsed && (
            <span className="sidebar-logo-text">AutoShop</span>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto scrollbar-custom py-4">
          <ul className="space-y-2">
            {filteredMenuItems?.map((item) => (
              <li key={item?.path}>
                <button
                  onClick={() => handleNavigation(item?.path)}
                  className={`
                    nav-item w-full
                    ${location?.pathname === item?.path ? 'active' : ''}
                  `}
                  title={isCollapsed ? item?.tooltip : ''}
                  aria-current={location?.pathname === item?.path ? 'page' : undefined}
                >
                  <span className="nav-item-icon">
                    <Icon name={item?.icon} size={20} />
                  </span>
                  {!isCollapsed && (
                    <span className="nav-item-label">{item?.label}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {!isCollapsed && (
          <div className="p-4 border-t border-border">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="User" size={20} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  Usuario
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {userRole}
                </p>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default RoleBasedSidebar;